import { useMemo } from 'react';
import { useGetCatalogQuery, useSearchQuery } from '@platform/state/api';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { useLinkCandidatesQuery } from './api/linkCandidatesApi';
import type { ILinkCandidates } from './ILinkCandidates';
import { linkCandidatePlan } from './linkCandidatePlan';
import { linkCandidateTypes } from './linkCandidateTypes';

/** Скільки рядків показує вікно (прототип lkAdv: rows.slice(0, 200)). */
export const LINK_LIMIT = 200;

/**
 * Кандидати для «Знайдіть елемент для зв’язку» (прототип lkAdv kind record): записи всіх модулів простору в порядку ws.modules, без самого
 * запису й уже пов'язаних (`excludeIds`), з пошуком `q` за назвою або кодом, вибраний чип `filter` — лише його тип. Без пошуку читаються
 * перші типи, яких вистачає на 200 рядків (кількість — з каталогу); з пошуком — спершу глобальний пошук за типами, далі сторінки типів зі
 * збігами. Кількість: без пошуку — записи всіх типів мінус виключені; з пошуком — збіги прочитаних типів і решти за глобальним пошуком.
 */
export function useLinkCandidates(ws: string, q: string, filter: string, excludeIds: readonly string[]): ILinkCandidates {
  const lang = useAppSelector(selectLang);
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws });
  const types = useMemo(() => linkCandidateTypes(catalog, lang), [catalog, lang]);
  const searchAll = Boolean(q) && !filter;
  const { data: search, isFetching: searching } = useSearchQuery({ q, workspaceId: ws, perGroup: 1, groupBy: 'recordType' }, { skip: !searchAll });
  const hits = useMemo(
    () => (searchAll && search ? new Map(search.groups.map((g) => [g.recordType ?? g.key ?? g.module, g.count])) : undefined), [searchAll, search],
  );
  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);
  const plan = useMemo(() => linkCandidatePlan(types, filter, LINK_LIMIT, exclude.size, hits), [types, filter, exclude.size, hits]);
  const ready = Boolean(catalog) && (!searchAll || Boolean(hits));
  const { data: pages, isFetching } = useLinkCandidatesQuery({ ws, types: plan, q }, { skip: !ready || !plan.length });
  return useMemo(() => {
    const read = pages ?? [];
    const rows = read.flatMap((p) => p.items).filter((r) => !exclude.has(r.id));
    const dropped = read.reduce((n, p) => n + p.items.filter((r) => exclude.has(r.id)).length, 0);
    const fetched = new Set(read.map((p) => p.rt));
    const own = read.reduce((n, p) => n + p.total, 0) - dropped;
    const total = filter ? own
      : hits ? own + [...hits].filter(([rt]) => !fetched.has(rt)).reduce((n, [, c]) => n + c, 0)
        : Math.max(0, types.reduce((n, t) => n + t.count, 0) - exclude.size);
    return { rows: rows.slice(0, LINK_LIMIT), total: ready ? total : 0, loading: !ready || searching || isFetching };
  }, [pages, exclude, filter, hits, types, ready, searching, isFetching]);
}
